import { Flex } from "@/components/atoms/flex";
import { useAllInteractionsStyle } from "@/components/templates/allInteractions/style";
import {
	mergeClasses,
	TableCell,
	TableRow,
	TableCellLayout,
	Button,
	Divider,
} from "@fluentui/react-components";
import { SupervisorTableHeader } from "@/components/molecules/supervisor";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import {
	TableType,
	ActiveInteractionsTableColumnName,
	TableColumnInfo,
} from "@/common/types";
import { InteractionDto, InteractionHandlingStatus } from "@/repository/models";
import { useTableStyle } from "@/components/atoms/customTable";
import { useCallback, useState, useMemo, useEffect } from "react";
import { useIntl } from "react-intl";
import {
	getInteractionStatusText,
	getInteractionSecondaryInfo,
} from "@/common/helpers/supervisorHelper";
import { getInteractionName } from "@/common/helpers";
import Timer from "@/components/atoms/timer/Timer";
import {
	ArrowCounterclockwise16Regular,
	ArrowUndo16Regular,
	SparkleFilled,
} from "@fluentui/react-icons";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { SkillTableCell } from "@/components/atoms/skillTableCell";
import { CustomerTableCell } from "@/components/atoms/customerTableCell";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { MonitorTypes } from "@/repository/models/monitoring";
import { useStyle } from ".";
import {
	InteractionFilterState,
	useInteractionFilterStore,
} from "@/common/stores/nonpersistingStores/interactionfilter";
import { CustomTable } from "@/components/atoms/customTable/CustomTable";
import { useTableColumns } from "@/hooks/tables";
import { CacheKey } from "@/common/stores/persistingStores/app";
import { useTagsExist } from "@/common/stores/persistingStores/app/appSettingsStore";
import { TagStack } from "@/components/atoms/tagStack";
import { InteractionIconOverlay } from "@/components/molecules/interactionIconOverlay";
import { InteractionPanelItemDescription } from "@/components/organisms/interaction/types";
import { StatusCell } from "@/components/atoms/statusCell";
import {
	useActiveInteractions,
	useActiveInteractionsActions,
} from "@/hooks/allInteractions";
import { useTransferActions } from "./useTransferAction";
import { TooltipEllipsisTableCell } from "@/components/atoms/tooltipEllipsisTableCell";
import { SentimentIndicator } from "@/components/organisms/supervisor/SentimentIndicator";

export const AllActiveInteractionsTable = () => {
	const intl = useIntl();

	const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const filterData = useInteractionFilterStore(
		(state: InteractionFilterState) => state.active
	);
	const { activeInteractions } = useActiveInteractions();
	const {
		getWhisperButton,
		getMonitoringActionButton,
		getMonitoringTypeButton,
		setMonitoringStartInProgress,
		getPickUpButton,
	} = useActiveInteractionsActions();

	const hasAnyQueueSkillRoutingEnabled = useMemo(() => {
		const activeQueueIds = new Set(activeInteractions?.map((x) => x.queueId));

		return queues.some(
			(q) => activeQueueIds.has(q.id) && q.isSkillBasedRoutingEnabled
		);
	}, [activeInteractions, queues]);

	const tagsExist = useTagsExist();

	const classes = useStyle();
	const interactionClasses = useInteractionStyle();
	const tableClasses = useTableStyle();
	const allInteractionsStyleClasses = useAllInteractionsStyle();

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	useEffect(() => {
		if (callSession) {
			setMonitoringStartInProgress(false);
		}
	}, [callSession]);

	const unHoverItem = () => {
		setHoveredItemKey(null);
	};

	const { openPopovers, getTextColdTransferButtons } =
		useTransferActions(unHoverItem);

	const renderActionsCell = useCallback(
		(item: InteractionDto) => {
			return (
				<TableCellLayout>
					{hoveredItemKey !== item.id && (
						<span
							className={interactionClasses.activeInteractionActionsContainer}
						>
							{getMonitoringTypeButton(item)}
						</span>
					)}
					{(hoveredItemKey === item.id || openPopovers[item.id]) && (
						<span
							className={interactionClasses.activeInteractionActionsContainer}
						>
							{getMonitoringActionButton(item, MonitorTypes.Silent)}
							{getMonitoringActionButton(item, MonitorTypes.BargeIn)}
							{getWhisperButton(item)}
							{item.isHandledByAiAgent && getPickUpButton(item)}
							{getTextColdTransferButtons(item)}
						</span>
					)}
				</TableCellLayout>
			);
		},
		[hoveredItemKey, openPopovers, queues, activeInteractions, callSession]
	);

	const activeTableColumnInfo: TableColumnInfo[] = useMemo<
		TableColumnInfo[]
	>(() => {
		return [
			{
				columnId: ActiveInteractionsTableColumnName.Icon,
				// 8px cell margin-left + 36px avatar (see iconTableColumn in
				// atoms/customTable/style.ts), flex-centred in the column, leaving ~16px
				// of slack so the icon never overflows into the Customer column.
				width: "60px",
				notResizable: true,
				notSortable: true,
				notRemovable: true,
				hideHeader: true,
			},
			{
				columnId: ActiveInteractionsTableColumnName.Customer,
				minWidth: 130,
				idealWidth: 180,
				fieldName: "name",
			},
			{
				columnId: ActiveInteractionsTableColumnName.Queue,
				minWidth: 130,
				idealWidth: 180,
				fieldName: "queueName",
			},
			{
				columnId: ActiveInteractionsTableColumnName.Skills,
				minWidth: 130,
				idealWidth: 180,
				compare: (itemA: InteractionDto, itemB: InteractionDto) => {
					let skillsA = itemA.skills?.flatMap((x) => x.name).join(",");
					let skillsB = itemB.skills?.flatMap((x) => x.name).join(",");

					return (skillsA ?? "").localeCompare(skillsB ?? "");
				},
			},
			{
				columnId: ActiveInteractionsTableColumnName.Tags,
				minWidth: 130,
				idealWidth: 180,
				// Tags are display-only in CC4ALL-8822 — not sortable, searchable or filterable.
				notSortable: true,
			},
			{
				columnId: ActiveInteractionsTableColumnName.Agent,
				minWidth: 130,
				idealWidth: 180,
				fieldName: "agentName",
			},
			{
				columnId: ActiveInteractionsTableColumnName.Timer,
				minWidth: 80,
				idealWidth: 80,
				fieldName: "startDate",
				sortingReversed: true,
			},
			{
				columnId: ActiveInteractionsTableColumnName.Sentiment,
				minWidth: 110,
				idealWidth: 130,
				renderCell: (item: InteractionDto) => (
					<TableCellLayout truncate>
						<SentimentIndicator sentiment={item.overallSentiment} />
					</TableCellLayout>
				),
				fieldName: "overallSentiment",
				compare: (itemA: InteractionDto, itemB: InteractionDto) => {
					return (itemA.overallSentiment ?? -1) - (itemB.overallSentiment ?? -1);
				},
			},
			{
				columnId: ActiveInteractionsTableColumnName.Actions,
				width: "120px",
				notResizable: true,
				notSortable: true,
				notRemovable: true,
				customRenderCell: renderActionsCell,
			},
		];
	}, [
		hoveredItemKey,
		queues,
		activeInteractions,
		hasAnyQueueSkillRoutingEnabled,
	]);

	// Columns the tenant configuration has nothing to show in. They are excluded rather than merely
	// hidden, so "add deleted column" and a settings reset cannot bring back an empty column.
	const excludedColumnIds = useMemo(() => {
		const excluded: string[] = [];

		if (!hasAnyQueueSkillRoutingEnabled) {
			excluded.push(ActiveInteractionsTableColumnName.Skills);
		}

		if (!tagsExist) {
			excluded.push(ActiveInteractionsTableColumnName.Tags);
		}

		return excluded;
	}, [hasAnyQueueSkillRoutingEnabled, tagsExist]);

	const {
		columns,
		removeColumn,
		addDeletedColumn,
		columnSizing,
		headerSortProps,
		rows,
		resetToDefaultTableSettings,
	} = useTableColumns<InteractionDto>(
		activeTableColumnInfo,
		activeInteractions || [],
		CacheKey.ActiveInteractionsTableSettings,
		{
			columnId: ActiveInteractionsTableColumnName.Customer,
			direction: "ascending",
		},
		excludedColumnIds
	);

	const queuesWithSkillsBasedRoutingEnabled = useMemo(() => {
		return new Set(
			queues.filter((x) => x.isSkillBasedRoutingEnabled).map((x) => x.id)
		);
	}, [queues]);

	const currentColumnIds = useMemo(() => {
		return columns.map(({ columnId }) => columnId);
	}, [columns]);

	const renderColumnCell = useCallback(
		(
			columnName: ActiveInteractionsTableColumnName,
			itemId: string,
			children: JSX.Element
		) => {
			return (
				currentColumnIds.includes(columnName) && (
					<TableCell key={`${columnName}-${itemId}`}>{children}</TableCell>
				)
			);
		},
		[currentColumnIds]
	);

	const getRowTemplate = useCallback(
		(item: InteractionDto) => {
			return (
				<TableRow
					onMouseLeave={() => {
						setHoveredItemKey(null);
					}}
					onMouseEnter={() => {
						setHoveredItemKey(item.id);
					}}
					key={item.id}
					className={mergeClasses(tableClasses.row, tableClasses.bodyText)}
				>
					<TableCell
						key={`${ActiveInteractionsTableColumnName.Icon}-${item.id}`}
						className={tableClasses.iconTableColumn}
					>
						<div
							className={mergeClasses(
								tableClasses.iconColumn,
								tableClasses.customAvatar
							)}
						>
							<InteractionIconOverlay
								interaction={item}
								itemPanelStatus={InteractionPanelItemDescription.Active}
								interactionHandlingStatus={InteractionHandlingStatus.Active}
								hideUnreadIcon={true}
								hideDirectionArrow
								isSkillBasedRoutingEnabled={queuesWithSkillsBasedRoutingEnabled.has(
									item.queueId
								)}
							/>
						</div>
					</TableCell>
					{renderColumnCell(
						ActiveInteractionsTableColumnName.Customer,
						item.id,
						<CustomerTableCell
							name={getInteractionName(item.type, item.name, intl)}
							contactInformation={getInteractionSecondaryInfo(item, intl)}
							direction={item.direction}
							displayAvatar={false}
						/>
					)}
					{renderColumnCell(
						ActiveInteractionsTableColumnName.Queue,
						item.id,
						<TableCellLayout truncate>{item.queueName}</TableCellLayout>
					)}
					{renderColumnCell(
						ActiveInteractionsTableColumnName.Skills,
						item.id,
						hasAnyQueueSkillRoutingEnabled ? (
							<SkillTableCell skills={item.skills} />
						) : (
							<></>
						)
					)}
					{renderColumnCell(
						ActiveInteractionsTableColumnName.Tags,
						item.id,
						<TagStack tags={item.assignedTags} />
					)}
					{currentColumnIds.includes(
						ActiveInteractionsTableColumnName.Agent
					) && (
						<TooltipEllipsisTableCell
							columnId={`${ActiveInteractionsTableColumnName.Agent}-${item.id}`}
							text={item.agentName?.length ? item.agentName : "-"}
							tableCellStyle={{ width: "18%" }}
							suffixIcon={
								item.isHandledByAiAgent ? <SparkleFilled /> : undefined
							}
						/>
					)}
					{renderColumnCell(
						ActiveInteractionsTableColumnName.Timer,
						item.id,
						<StatusCell
							status={getInteractionStatusText(item.status, intl)}
							timer={<Timer startDate={item?.startDate} />}
						/>
					)}
					{renderColumnCell(
						ActiveInteractionsTableColumnName.Sentiment,
						item.id,
						<TableCellLayout truncate>
							<SentimentIndicator
								sentiment={item.overallSentiment}
							/>
						</TableCellLayout>
					)}
					<TableCell
						key={`${ActiveInteractionsTableColumnName.Actions}-${item.sessionId}`}
					>
						{renderActionsCell(item)}
					</TableCell>
				</TableRow>
			);
		},
		[hoveredItemKey, columns, queues, renderActionsCell]
	);

	const isAddDeletedColumnDisabled = () => {
		const currentActiveTableColumnInfoLength = activeTableColumnInfo.filter(
			(column) => !excludedColumnIds.includes(column.columnId.toString())
		).length;

		return columns.length === currentActiveTableColumnInfoLength;
	};

	return (
		<Flex
			column
			className={mergeClasses(
				allInteractionsStyleClasses.rightTable,
				interactionClasses.activeBackground
			)}
		>
			<SupervisorTableHeader
				type={TableType.Active}
				count={activeInteractions?.length ?? 0}
				className={{ activeContainer: classes.container }}
				filterData={filterData}
				additionalActions={[
					<Button
						appearance="transparent"
						onClick={addDeletedColumn}
						title={intl.formatMessage({
							id: "Interaction.TableActions.AddDeletedColumn",
						})}
						className={classes.additionalActionButton}
						disabled={isAddDeletedColumnDisabled()}
					>
						<ArrowUndo16Regular />
					</Button>,
					<Button
						appearance="transparent"
						onClick={resetToDefaultTableSettings}
						title={intl.formatMessage({
							id: "Interaction.TableActions.ResetSettings",
						})}
						className={classes.additionalActionButton}
					>
						<ArrowCounterclockwise16Regular />
					</Button>,
					<Divider vertical />,
				]}
			/>
			<div className={classes.tableContainer} style={{ overflowX: "auto" }}>
				{activeInteractions && (
					<CustomTable
						columns={columns}
						columnSizing={columnSizing}
						headerSortProps={headerSortProps}
						rows={rows}
						getRowTemplateCallback={getRowTemplate}
						isStickyHeader
						resizable={true}
						sortable={true}
						columnsRemovable={true}
						removeColumn={removeColumn}
						headerTranslationPrefix={"Interaction.Table"}
						classNames={{
							headerCell: classes.tableHeaderCell,
						}}
					/>
				)}
			</div>
		</Flex>
	);
};
